import React from 'react';
import { useNavigate } from 'react-router-dom';

const TrialBlockedPage = ({ status, trialData }) => {
  const navigate = useNavigate();

  const renderContent = () => {
    switch (status) {
      case 'no_trial':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Trial Not Setup</h2>
            <p className="mt-2 text-sm text-gray-600">
              Your trial account has not been configured yet.
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Please contact support to set up your trial access.
            </p>
            <div className="mt-6">
              <a href="mailto:support@internhub.com" className="text-blue-600 hover:text-blue-500">
                support@internhub.com
              </a>
            </div>
          </div>
        );

      case 'pending':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
              <svg
                className="h-6 w-6 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Trial Pending</h2>
            <p className="mt-2 text-sm text-gray-600">
              Your trial will begin on{' '}
              {trialData?.trial_start_date
                ? new Date(trialData.trial_start_date).toLocaleDateString()
                : 'soon'}
              .
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Please check back then to access InternHub.
            </p>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
              <svg
                className="h-6 w-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Trial Expired</h2>
            <p className="mt-2 text-sm text-gray-600">
              Your trial expired on{' '}
              {trialData?.trial_end_date
                ? new Date(trialData.trial_end_date).toLocaleDateString()
                : 'recently'}
              .
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Contact us to extend your access or upgrade to a full account.
            </p>
            <div className="mt-6">
              <a href="mailto:support@internhub.com" className="text-blue-600 hover:text-blue-500">
                support@internhub.com
              </a>
            </div>
          </div>
        );

      case 'revoked':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Access Revoked</h2>
            <p className="mt-2 text-sm text-gray-600">
              Your trial access has been revoked by an administrator.
            </p>
            {trialData?.notes && trialData.notes.includes('[REVOKED]') && (
              <p className="mt-2 text-sm text-gray-600">
                Reason: {trialData.notes.split('[REVOKED]: ')[1]?.split('\n')[0]}
              </p>
            )}
            <p className="mt-4 text-sm text-gray-600">
              If you believe this is an error, please contact support.
            </p>
            <div className="mt-6">
              <a href="mailto:support@internhub.com" className="text-blue-600 hover:text-blue-500">
                support@internhub.com
              </a>
            </div>
          </div>
        );

      case 'inactive':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
              <svg
                className="h-6 w-6 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Trial Not Active</h2>
            <p className="mt-2 text-sm text-gray-600">Your trial is currently not active.</p>
            <p className="mt-2 text-sm text-gray-600">
              Please contact us to resolve this issue and restore your access.
            </p>
            <div className="mt-6">
              <a href="mailto:support@internhub.com" className="text-blue-600 hover:text-blue-500">
                support@internhub.com
              </a>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {renderContent()}

        <div className="mt-8 text-center">
          <button
            onClick={() => {
              localStorage.clear();
              navigate('/login');
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Sign out and try a different account
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialBlockedPage;
